/**
 * Subscribe a component to the feature flags — LV §181, §134.
 *
 * The same problem `auth/useSession.ts` solves, for the same reason and with the same
 * primitive. The flags live in a module variable so the sync engine and the
 * service-worker registration can read them outside a component tree, and
 * `isFeatureEnabled()` is therefore a plain function call: it returns whatever was
 * true at render time and nothing re-renders when the server's answer arrives.
 *
 * That produced a visible defect, found by rendering the page rather than by a test.
 * `Login` marks the session authenticated from *two* places — the `bootstrapSession`
 * result and a subscription to the token store — and the token subscription fires
 * first, while `GET /configuration/feature-flags` is still in flight. The shell
 * therefore mounted with the pre-answer values, and with `FEATURE_OFFLINE=false` the
 * sidebar still offered "Offline & Synchronisation" and the header still claimed
 * "Synchronisiert". The `/sync` route itself refused correctly, because navigating
 * re-rendered it — which is exactly the kind of half-working state a passing test
 * suite cannot see.
 *
 * `useSyncExternalStore` compares snapshots by identity, so `getFeatureFlags()` must
 * return the same object until the values actually change. It does; see the note there.
 */

import { useSyncExternalStore } from "react";

import { getFeatureFlags, onFeatureFlags } from "@/app/feature-flags";
import type { FeatureFlags } from "@/types/api";

/** Re-renders the caller whenever the server's answer arrives or changes. */
export function useFeatureFlags(): FeatureFlags {
  return useSyncExternalStore(onFeatureFlags, getFeatureFlags, getFeatureFlags);
}
